import { useMemo, useState } from "react";
import Button from "components/button";
import LoaderButton from "components/button/LoaderButton";
import Modal from "components/modal";
import { useDispatch, useSelector } from "react-redux";
import { changeOrderStatus } from "store/orderSlice";
import { ORDER_STATUS } from "constants";
import { useNavigate } from "react-router-dom";
import { showToast } from "components/toast";
import { resetOrderStatusError } from "store/orderSlice";

const OrderStatusModal = ({ data, refreshList, isRedirect }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { orderChangeStatusError, orderChangeStatusLoading, orderStatusData } =
    useSelector((state) => state.orders);

  const [isOpen, setIsOpen] = useState(false);

  const openModal = () => setIsOpen(true);

  const closeModal = () => {
    setIsOpen(false);
    dispatch(resetOrderStatusError());
  };

  const placedOrderData = useMemo(() => {
    if (!orderStatusData?.length) return null;
    return (
      orderStatusData.find((item) => item.enumVal === ORDER_STATUS.PLACED) ||
      null
    );
  }, [orderStatusData]);

  const handlePlaceOrder = async () => {
    if (!data?._id || !placedOrderData?._id) return;

    try {
      const res = await dispatch(
        changeOrderStatus({ _id: data._id, status: placedOrderData._id })
      ).unwrap(); // ✅ unwrap gives the actual API response

      if (res?.statuscode === 200) {
        showToast.success(`Order ${placedOrderData.name} successfully!`);

        if (isRedirect) {
          navigate(`/orders/${data._id}`);
        }

        refreshList?.();
        closeModal(); // ✅ close only on success
      } else {
        showToast.error(
          res?.msg ||
            res?.message ||
            orderChangeStatusError ||
            "Failed to place order."
        );
        // ❌ don't close modal on error
      }
    } catch (err) {
      console.log('err: ', err);
      showToast.error(
        err ||
          orderChangeStatusError ||
          "Something went wrong."
      );
      // ❌ don't close modal on error
    dispatch(resetOrderStatusError());

    }

    return (
      <Modal isOpen={isOpen} onClose={closeModal}>
        {/* Example footer buttons */}
        <div className="flex justify-end gap-2">
          <Button onClick={closeModal} variant="secondary">
            Cancel
          </Button>
          <LoaderButton
            loading={orderChangeStatusLoading}
            onClick={handlePlaceOrder}
          >
            Place Order
          </LoaderButton>
        </div>
      </Modal>
    );
  };

  return (
    <>
      <Button type="button" variant="green" onClick={openModal}>
        Place Order
      </Button>

      {/* Modal */}
      <Modal isOpen={isOpen} onClose={closeModal} className="max-w-lg">
        <h2 className="border-b py-3 pl-2 text-xl font-bold text-navy-700 dark:text-white">
          Confirm Order
        </h2>

        <div className="relative w-full rounded-3xl p-4 lg:p-5">
          <p className="mb-6 text-sm text-gray-600 dark:text-gray-400">
            Are you sure you want to place this order?
          </p>

          {orderChangeStatusError && (
            <div className="mt-2">
              <p className="text-sm text-red-500">{orderChangeStatusError}</p>
            </div>
          )}

          <div className="mt-6 flex items-center justify-end gap-3">
            <Button type="button" variant="outline" onClick={closeModal}>
              Cancel
            </Button>

            <LoaderButton
              type="button"
              isLoading={orderChangeStatusLoading}
              onClick={handlePlaceOrder}
              disabled={!placedOrderData?._id}
            >
              Confirm & Place
            </LoaderButton>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default OrderStatusModal;
